import React, { useState, useEffect, useRef } from 'react';
import { uploadFile, apiClient } from '../../api/client';
import {
  SECTION_KEYS,
  SECTION_LABELS,
  ALLOWED_MIME_TYPES,
  MAX_UPLOAD_SIZE,
  SectionKey,
} from '../attendee-mobile/constants';
import { DEFAULT_PANEL_IMAGES } from '../../assets/panel-defaults';
import {
  PRIMARY,
  TEXT_DARK,
  TEXT_MUTED,
  BORDER_LIGHT,
  BG_CARD,
} from '../legacy-ball/table-management/designTokens';

interface PanelImageEntry {
  url: string;
  sizeKB: number;
}

interface PanelImagesApiResponse {
  images: Record<string, PanelImageEntry | null>;
}

interface UploadResponse {
  sectionKey: string;
  url: string;
  sizeKB: number;
}

/**
 * Formats a byte count as whole KB (e.g., "142 KB").
 */
export function formatFileSize(bytes: number): string {
  return `${Math.round(bytes / 1024)} KB`;
}

/**
 * Admin panel for managing header panel background images for each
 * of the 9 Attendee mobile navigation sections.
 */
export function AdminImagePanel() {
  const [images, setImages] = useState<Record<string, PanelImageEntry | null>>({});
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [uploading, setUploading] = useState<Record<string, boolean>>({});
  const fileInputRefs = useRef<Record<string, HTMLInputElement | null>>({});

  useEffect(() => {
    fetchPanelImages();
  }, []);

  async function fetchPanelImages() {
    try {
      const response = await apiClient<PanelImagesApiResponse>('/panel-images');
      setImages(response.images);
    } catch {
      // Graceful degradation — show all defaults
      setImages({});
    } finally {
      setLoading(false);
    }
  }

  function clearError(sectionKey: string) {
    setErrors((prev) => {
      const next = { ...prev };
      delete next[sectionKey];
      return next;
    });
  }

  function setError(sectionKey: string, message: string) {
    setErrors((prev) => ({ ...prev, [sectionKey]: message }));
  }

  function validateFile(file: File): string | null {
    if (!(ALLOWED_MIME_TYPES as readonly string[]).includes(file.type)) {
      return 'Accepted formats: JPEG, PNG, WebP';
    }
    if (file.size > MAX_UPLOAD_SIZE) {
      return 'Maximum file size: 10MB';
    }
    return null;
  }

  async function handleUpload(sectionKey: SectionKey, file: File) {
    clearError(sectionKey);

    const validationError = validateFile(file);
    if (validationError) {
      setError(sectionKey, validationError);
      return;
    }

    setUploading((prev) => ({ ...prev, [sectionKey]: true }));

    try {
      const response = await uploadFile<UploadResponse>(
        `/panel-images/${sectionKey}`,
        file,
        'file',
      );
      setImages((prev) => ({
        ...prev,
        [sectionKey]: { url: response.url, sizeKB: response.sizeKB },
      }));
    } catch {
      setError(sectionKey, 'Upload failed. Please try again.');
    } finally {
      setUploading((prev) => ({ ...prev, [sectionKey]: false }));
    }
  }

  async function handleRemove(sectionKey: SectionKey) {
    const confirmed = window.confirm(
      `Remove the custom image for "${SECTION_LABELS[SECTION_KEYS.indexOf(sectionKey)]}"? It will revert to the default.`,
    );
    if (!confirmed) return;

    clearError(sectionKey);

    try {
      await apiClient(`/panel-images/${sectionKey}`, { method: 'DELETE' });
      setImages((prev) => {
        const next = { ...prev };
        delete next[sectionKey];
        return next;
      });
    } catch {
      setError(sectionKey, 'Failed to remove image. Please try again.');
    }
  }

  function handleFileChange(sectionKey: SectionKey, e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) {
      handleUpload(sectionKey, file);
    }
    // Reset input so the same file can be re-selected
    e.target.value = '';
  }

  if (loading) {
    return <p style={{ fontSize: '0.85rem', color: TEXT_MUTED }}>Loading panel images...</p>;
  }

  return (
    <div>
      <p style={{ fontSize: '0.9rem', color: TEXT_MUTED, marginBottom: '16px' }}>
        Upload custom background images for each section of the Attendee mobile header panel.
        Images are automatically optimized for mobile delivery.
      </p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {SECTION_KEYS.map((key, index) => {
          const entry = images[key];
          const apiBase = import.meta.env.VITE_API_URL || (typeof window !== 'undefined' ? window.location.origin + '/api' : 'http://localhost:3000');
          const thumbnailUrl = entry?.url
            ? (entry.url.startsWith('http') ? entry.url : `${apiBase}${entry.url}`)
            : DEFAULT_PANEL_IMAGES[key];
          const isUploading = uploading[key] ?? false;
          const error = errors[key];
          const hasCustomImage = !!entry;

          return (
            <div
              key={key}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '10px 12px',
                backgroundColor: BG_CARD,
                border: `1px solid ${BORDER_LIGHT}`,
                borderRadius: '8px',
              }}
            >
              {/* Thumbnail preview */}
              <img
                src={thumbnailUrl}
                alt={`${SECTION_LABELS[index]} panel image`}
                style={{
                  width: '80px',
                  height: '40px',
                  objectFit: 'cover',
                  borderRadius: '4px',
                  border: `1px solid ${BORDER_LIGHT}`,
                  flexShrink: 0,
                }}
              />

              {/* Section name and file size */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <p
                  style={{
                    margin: 0,
                    fontSize: '0.85rem',
                    fontWeight: 600,
                    color: TEXT_DARK,
                  }}
                >
                  {SECTION_LABELS[index]}
                </p>
                {hasCustomImage && entry.sizeKB > 0 && (
                  <p
                    style={{
                      margin: '2px 0 0',
                      fontSize: '0.75rem',
                      color: TEXT_MUTED,
                    }}
                  >
                    {formatFileSize(entry.sizeKB * 1024)}
                  </p>
                )}
                {error && (
                  <p
                    style={{
                      margin: '4px 0 0',
                      fontSize: '0.75rem',
                      color: '#d32f2f',
                    }}
                  >
                    {error}
                  </p>
                )}
              </div>

              {/* Upload and Remove controls */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                <input
                  ref={(el) => { fileInputRefs.current[key] = el; }}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={(e) => handleFileChange(key, e)}
                  style={{ display: 'none' }}
                  aria-label={`Upload image for ${SECTION_LABELS[index]}`}
                />
                <button
                  onClick={() => fileInputRefs.current[key]?.click()}
                  disabled={isUploading}
                  style={{
                    padding: '6px 12px',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    backgroundColor: PRIMARY,
                    color: '#fff',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: isUploading ? 'not-allowed' : 'pointer',
                    opacity: isUploading ? 0.6 : 1,
                    whiteSpace: 'nowrap',
                  }}
                  aria-label={`Upload image for ${SECTION_LABELS[index]}`}
                >
                  {isUploading ? 'Uploading...' : 'Upload'}
                </button>

                {hasCustomImage && (
                  <button
                    onClick={() => handleRemove(key)}
                    disabled={isUploading}
                    style={{
                      padding: '6px 10px',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      backgroundColor: '#fff',
                      color: '#d32f2f',
                      border: '1px solid #d32f2f',
                      borderRadius: '4px',
                      cursor: isUploading ? 'not-allowed' : 'pointer',
                      opacity: isUploading ? 0.6 : 1,
                      whiteSpace: 'nowrap',
                    }}
                    aria-label={`Remove image for ${SECTION_LABELS[index]}`}
                  >
                    Remove
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
